// The figure lab, `?scene=figure` (phase A3, gates A3.1 to A3.3): the cast built from their cards in the low-poly
// look, on a small test ground with slopes and steps, the Wanderer driven around a loop by a scripted route (the
// stand-in for the controller, through `FigureDriver`), the townsfolk and enemies idling beside it. It gives the
// handle `__rpg.figure`: fixed-camera shots, the motion's report (drift, look, springs), weapons, a look target, and
// the perf tour over the route. Everything it draws uses the look's one surface material, compiled at boot.
import * as THREE from "three";
import { createCollisionWorld, type CollisionWorld, type Rapier } from "@rpg/collision";
import type { ContentRegistry } from "@rpg/content";
import { buildFigure, createMotion, type Attachment, type Figure, type FigureCard, type Locomotion, type Motion, type MotionReport } from "@rpg/figure";
import { createLowPolyLook, type LowPolyLook, type LowPolySceneLook, type LowPolyTier } from "@rpg/look";
import { facet, merge, seeded } from "@rpg/meshkit";
import { overlay, perfReport, runTour, type TourSegment } from "@rpg/perf";
import type { Runtime } from "@rpg/runtime";
import type { ContentTables } from "../content";
import { createRouteDriver, type RouteTuning } from "./locomotion";

/** The lab's ground: low hills, a flat plateau at the middle for the shots, rising at the rim. */
export function labHeight(x: number, z: number): number {
  const r = Math.hypot(x, z);
  const hills = 0.55 * Math.sin(x * 0.19) * Math.cos(z * 0.16) + 0.22 * Math.sin(x * 0.41 + z * 0.33);
  const flat = THREE.MathUtils.smoothstep(r, 2.5, 5.5);
  return hills * flat + (r > 17 ? (r - 17) * 0.45 : 0);
}

/** The scripted route (test data for the lab, not game tuning): idle, walk, walk with the staff out, run, a jump with a glide, run, slow down. */
export const LAB_ROUTE: RouteTuning = {
  loop: { x: 0, z: 0, rx: 9.5, rz: 8 },
  accel: 9,
  jump: 5.5,
  gravity: 12,
  sink: 1.3,
  legs: [
    { kind: "move", speed: 0, seconds: 1.2 },
    { kind: "move", speed: 1.3, seconds: 4 },
    { kind: "move", speed: 1.3, seconds: 2.5, acting: true },
    { kind: "move", speed: 4.6, seconds: 3 },
    { kind: "jump", speed: 4.6, glide: 0.6 },
    { kind: "move", speed: 4.6, seconds: 2 },
    { kind: "move", speed: 0.9, seconds: 2 },
  ],
};

/** Where the spectators stand (x, z, yaw) on the ground beside the loop. */
const SPECTATORS: Record<string, [number, number, number]> = {
  villager: [-4.2, 3.4, 2.4],
  elder: [-5.1, 2.2, 2.0],
  child: [-3.4, 4.4, 2.6],
  shade: [12.6, -2.2, -1.6],
  brute: [12.8, 1.4, -1.4],
  wisp: [11.6, 3.6, -1.9],
  // phase B4: the mini-boss in both its looks, beside the other enemies
  warden: [14.6, -0.6, -1.5],
  "warden-wrath": [15.4, 3.2, -1.7],
  // phase D1 (the town): the smith and the merchant beside the other townsfolk
  smith: [-6.0, 0.8, 1.7],
  merchant: [-2.4, 5.5, 2.8],
};
/** The steps on the loop (angle around it, radians), 12 cm high. */
const STEPS = [0.9, 3.7];
const STEP_H = 0.12;

type CardRow = ContentTables["cards"];
type AttachmentRow = ContentTables["attachments"];

/** One character in the lab. */
interface Member {
  id: string;
  row: CardRow;
  fig: Figure;
  motion: Motion;
  home: [number, number, number];
}

/** The shots the lab can frame. */
export const SHOTS = ["front", "back", "threeQuarter", "close", "play", "lineup", "weapons"] as const;
export type ShotName = (typeof SHOTS)[number];

export interface FigureLabOptions {
  runtime: Runtime;
  renderer: THREE.WebGLRenderer;
  rapier: Rapier;
  registry: ContentRegistry<ContentTables>;
  tier: string;
  /** The scene look's id (default valley-day). */
  look?: string;
}

/** The mounted lab. */
export interface FigureLab {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  /** Where the sim scene's own meshes go (not drawn in the lab). */
  offstage: THREE.Scene;
  render(): void;
  /** A summary for `state()` (checked finite after every step). */
  state(): { mode: string; hero: { x: number; y: number; z: number }; motion: string; drift: number };
  handle: ReturnType<typeof labHandle>;
}

/** Mount the lab. */
export function mountFigureLab(o: FigureLabOptions): FigureLab {
  const { runtime, renderer, registry } = o;
  const sceneLook = registry.get("looks", o.look ?? "valley-day") as unknown as LowPolySceneLook | undefined;
  if (!sceneLook) throw new Error(`figure lab: no look "${o.look ?? "valley-day"}"`);
  const tierRow = registry.get("tiers", o.tier === "low" ? "low" : "high") as unknown as LowPolyTier | undefined;
  if (!tierRow) throw new Error(`figure lab: no tier "${o.tier}"`);
  const scene = new THREE.Scene();
  scene.name = "figure-lab";
  const camera = new THREE.PerspectiveCamera(50, innerWidth / innerHeight, 0.1, 400);
  const look = createLowPolyLook({ renderer, scene, camera, look: sceneLook, tier: tierRow, shadowExtent: 14 });

  // ── the ground, the steps and a few trees, merged into one static mesh; the same triangles as colliders ──
  const world = createCollisionWorld(o.rapier, { dt: runtime.clock.dt, layers: { ground: [] } });
  const ground = buildGround(look, world);
  scene.add(ground);
  look.track(ground);
  world.step(); // queries see the world after a step
  const probe = (x: number, z: number, fromY: number): number | null => world.castRay({ x, y: fromY, z }, { x: 0, y: -1, z: 0 }, 40, { layers: ["ground"] })?.point.y ?? null;
  const groundAt = (x: number, z: number): number => probe(x, z, 30) ?? labHeight(x, z);

  // ── the cast, from their cards ──
  const attachments = new Map<string, Attachment>(registry.rows("attachments").map((a: AttachmentRow) => [a.id, a as unknown as Attachment]));
  const cast = new Map<string, Member>();
  for (const row of registry.rows("cards")) {
    const card = row as unknown as FigureCard;
    const fig = buildFigure(card, { material: look.surface, colour: look.palette });
    const first = row.attachments?.[0];
    if (first && card.socket) fig.attach(attachments.get(first.id) ?? null);
    const motion = createMotion(fig, card.motion!, probe);
    const home = SPECTATORS[row.id] ?? [0, 0, 0];
    fig.root.name = `figure:${row.id}`;
    scene.add(fig.root);
    look.track(fig.root, { blob: row.blob });
    cast.set(row.id, { id: row.id, row, fig, motion, home });
  }
  const found = cast.get("wanderer");
  if (!found) throw new Error("figure lab: no wanderer card");
  const hero: Member = found;
  for (const m of cast.values()) if (m !== hero) place(m, m.home[0], m.home[1], m.home[2]);

  function place(m: Member, x: number, z: number, yaw: number): void {
    m.fig.root.position.set(x, groundAt(x, z), z);
    m.fig.root.rotation.y = yaw;
    m.motion.reset();
  }

  // ── the hero's driver: the scripted route (the controller's seam) ──
  const route = createRouteDriver(LAB_ROUTE, groundAt);
  runtime.on("sim", (c) => route.step(c.dt), { name: "figure: route" });

  // ── shots and camera ──
  let mode: "follow" | ShotName = "follow";
  let override: { x: number; z: number; yaw: number } | null = null;
  let lookAt: THREE.Vector3 | null = null;
  let acting: boolean | null = null;
  const extras: Figure[] = [];
  const sunFlat = new THREE.Vector3(Math.sin(sceneLook.sun.azimuth), 0, Math.cos(sceneLook.sun.azimuth)).normalize();
  const yawToward = (dx: number, dz: number): number => Math.atan2(dx, dz);
  const camTarget = new THREE.Vector3();
  const camPos = new THREE.Vector3();
  let camPrimed = false;

  const still: Locomotion = { speed: 0, velocity: { x: 0, y: 0, z: 0 }, grounded: true, jump: false, glide: false };
  const heroInput = (): Locomotion => {
    const base = override ? still : route.locomotion();
    return { ...base, acting: acting ?? base.acting, lookAt: lookAt ? { x: lookAt.x, y: lookAt.y, z: lookAt.z } : null };
  };

  const frameCamera = (dt: number): void => {
    const h = hero.fig.root.position;
    if (mode === "follow" || mode === "play") {
      const yaw = hero.fig.root.rotation.y;
      const fwd = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
      const want = h.clone().addScaledVector(fwd, -4.4).add(new THREE.Vector3(0, 1.75, 0));
      const tgt = h.clone().add(new THREE.Vector3(0, 1.0, 0)).addScaledVector(fwd, 2.2);
      const k = camPrimed ? 1 - Math.exp(-5 * dt) : 1;
      camPos.lerp(want, k);
      camTarget.lerp(tgt, k);
      camPrimed = true;
      camera.fov = 50;
    }
    camera.position.copy(camPos);
    camera.lookAt(camTarget);
    camera.updateProjectionMatrix();
  };

  runtime.on(
    "late",
    (c) => {
      // a long frame (a test's advance) poses in steps of at most 1/60 s, so the springs and gait see real time
      const n = Math.min(90, Math.max(1, Math.ceil(c.dt * 60 - 1e-6)));
      for (let i = 0; i < n; i++) pose(c.dt / n);
    },
    { name: "figure: pose" },
  );
  function pose(dt: number): void {
    {
      if (override) {
        hero.fig.root.position.set(override.x, groundAt(override.x, override.z), override.z);
        hero.fig.root.rotation.y = override.yaw;
      } else {
        const p = route.pose();
        hero.fig.root.position.set(p.x, p.y, p.z);
        hero.fig.root.rotation.y = p.yaw;
      }
      hero.motion.update(dt, heroInput());
      const hp = hero.fig.root.position;
      for (const m of cast.values()) {
        if (m === hero) continue;
        const npc = m.row.tier !== "enemy";
        m.motion.update(dt, { ...still, lookAt: npc && mode === "follow" ? { x: hp.x, y: hp.y + 1.3, z: hp.z } : null });
      }
      for (const f of extras) f.root.updateMatrixWorld(true);
      frameCamera(dt);
      look.focus(mode === "lineup" || mode === "weapons" ? camTarget : hp);
    }
  }

  const setCamera = (target: THREE.Vector3, dir: THREE.Vector3, dist: number, height: number, fov: number): void => {
    camTarget.copy(target);
    camPos.copy(target).addScaledVector(dir, dist).add(new THREE.Vector3(0, height, 0));
    camera.fov = fov;
  };
  const clearExtras = (): void => {
    for (const f of extras.splice(0)) {
      look.untrack(f.root);
      f.dispose();
    }
  };

  /** Frame a shot (the hero held still, the springs reset); null goes back to the route. */
  const shot = (name: ShotName | null): void => {
    clearExtras();
    acting = null;
    for (const m of cast.values()) m.fig.root.visible = true;
    if (name === null || name === "play") {
      override = null;
      route.hold(false);
      mode = name === "play" ? "play" : "follow";
      for (const m of cast.values()) if (m !== hero) place(m, m.home[0], m.home[1], m.home[2]);
      camPrimed = false;
      if (name === "play") {
        // a walking moment on the route, the camera at play distance behind the hero
        route.reset();
        for (let i = 0; i < 300; i++) route.step(runtime.clock.dt);
      }
      return;
    }
    mode = name;
    route.hold(true);
    const camDir = sunFlat.clone(); // the camera on the sun's side (decision 0036: lit from the camera's side)
    const face = yawToward(camDir.x, camDir.z);
    const centre = new THREE.Vector3(0, 0, 0);
    const turn = { front: 0, back: Math.PI, threeQuarter: -0.6, close: 0, lineup: 0, weapons: 0.35 }[name];
    override = { x: 0, z: 0, yaw: face + turn };
    const heroOnly = name === "front" || name === "back" || name === "threeQuarter" || name === "close";
    for (const m of cast.values()) if (m !== hero) m.fig.root.visible = !heroOnly;
    if (name === "lineup") {
      const side = new THREE.Vector3(camDir.z, 0, -camDir.x);
      const order = ["wanderer", "villager", "elder", "child", "shade", "brute", "wisp"];
      order.forEach((id, i) => {
        const m = cast.get(id);
        if (!m) return;
        const p = side.clone().multiplyScalar((i - 3) * 1.15);
        if (m === hero) override = { x: p.x, z: p.z, yaw: face + 0.3 };
        else place(m, p.x, p.z, face + 0.3 - i * 0.02);
      });
      setCamera(centre.clone().add(new THREE.Vector3(0, 0.8, 0)), camDir, 11, 0.6, 32);
      placeHero();
      return;
    }
    for (const m of cast.values()) if (m !== hero) place(m, m.home[0], m.home[1], m.home[2]);
    if (name === "weapons") {
      // the hero with each weapon on its socket, arms out: three figures from the same card
      const side = new THREE.Vector3(camDir.z, 0, -camDir.x);
      const ids = ["staff", "sword", "wand"];
      acting = true;
      hero.fig.attach(attachments.get("staff") ?? null);
      ids.slice(1).forEach((id, i) => {
        const f = buildFigure(hero.row as unknown as FigureCard, { material: look.surface, colour: look.palette });
        f.attach(attachments.get(id) ?? null);
        const motion = createMotion(f, (hero.row as unknown as FigureCard).motion!, probe);
        const p = side.clone().multiplyScalar((i + 1) * 1.3);
        f.root.position.set(p.x, groundAt(p.x, p.z), p.z);
        f.root.rotation.y = face + 0.35;
        scene.add(f.root);
        look.track(f.root, { blob: hero.row.blob });
        motion.reset();
        for (let k = 0; k < 120; k++) motion.update(1 / 60, { ...still, acting: true });
        extras.push(f);
      });
      setCamera(side.clone().multiplyScalar(1.3).add(new THREE.Vector3(0, 0.85, 0)), camDir, 7.5, 0.35, 30);
      placeHero();
      return;
    }
    if (name === "close") setCamera(new THREE.Vector3(0, 1.38, 0), camDir, 1.7, 0.05, 30);
    else setCamera(new THREE.Vector3(0, 0.85, 0), camDir, 5.2, 0.3, 30);
    placeHero();
  };
  /** Put the hero at its shot pose now, and plant it there. */
  const placeHero = (): void => {
    if (!override) return;
    hero.fig.root.position.set(override.x, groundAt(override.x, override.z), override.z);
    hero.fig.root.rotation.y = override.yaw;
    hero.motion.reset();
  };

  const render = (): void => look.render();
  look.warm();

  const lab: FigureLab = {
    scene,
    camera,
    offstage: new THREE.Scene(),
    render,
    state: () => ({ mode, hero: { x: hero.fig.root.position.x, y: hero.fig.root.position.y, z: hero.fig.root.position.z }, motion: hero.motion.state(), drift: hero.motion.report().drift.max }),
    handle: labHandle({
      runtime,
      renderer,
      look,
      cast,
      hero,
      route,
      attachments,
      shot,
      setLook: (p) => (lookAt = p ? new THREE.Vector3(p.x, p.y, p.z) : null),
      setActing: (on) => (acting = on),
      tier: o.tier,
      scene,
    }),
  };
  addEventListener("resize", () => {
    camera.aspect = innerWidth / innerHeight;
    camera.updateProjectionMatrix();
    look.resize(innerWidth, innerHeight);
  });
  return lab;
}

/** The handle's `figure` key. */
function labHandle(d: {
  runtime: Runtime;
  renderer: THREE.WebGLRenderer;
  look: LowPolyLook;
  cast: Map<string, Member>;
  hero: Member;
  route: ReturnType<typeof createRouteDriver>;
  attachments: Map<string, Attachment>;
  shot: (name: ShotName | null) => void;
  setLook: (p: { x: number; y: number; z: number } | null) => void;
  setActing: (on: boolean | null) => void;
  tier: string;
  scene: THREE.Scene;
}) {
  return {
    /** Frame a fixed-camera shot ("front", "back", "threeQuarter", "close", "play", "lineup", "weapons"), or null for the route. */
    shot: (name: ShotName | null) => d.shot(name),
    /** The hero's motion report (state, feet, look, drift, springs, history). */
    report: (): MotionReport => d.hero.motion.report(),
    /** Start a drift measurement. */
    resetDrift: () => d.hero.motion.resetDrift(),
    /** Restart the route from its start. */
    restart: () => {
      d.route.reset();
      d.hero.motion.reset();
    },
    /** The route's state. */
    route: () => d.route.state(),
    /** Stop the hero where it stands (true) or go on along the route (false). */
    hold: (on: boolean) => d.route.hold(on),
    /** A world point for the hero to look at, or null. */
    lookAt: (p: { x: number; y: number; z: number } | null) => d.setLook(p),
    /** Arms out (true), in (false) or as the route says (null). */
    acting: (on: boolean | null) => d.setActing(on),
    /** Put an attachment in the hero's hand (an id from the attachments table) or none. */
    weapon: (id: string | null) => d.hero.fig.attach(id ? (d.attachments.get(id) ?? null) : null),
    /** Each character's build: draw calls, triangles, vertices. */
    cast: () => Object.fromEntries([...d.cast].map(([id, m]) => [id, m.fig.stats()])),
    /** The look: tier, post passes, shadow map, blobs. */
    look: () => d.look.report(),
    /** The renderer's counts for the last frame. */
    counts: () => ({ calls: d.renderer.info.render.calls, triangles: d.renderer.info.render.triangles, programs: d.renderer.info.programs?.length ?? 0 }),
    /** Run the perf tour over the route (fast-forward `ffMs` game ms per drawn frame); read `perf()` until done. */
    tour: (ffMs = 200) => {
      const ui = overlay(d.tier);
      document.getElementById("perf-tour")?.setAttribute("data-no-strings", "");
      const t0 = { s: 0 };
      const segs: TourSegment[] = [
        { name: "warmup", start: () => d.shot(null), tick: (t) => t - t0.s > 1, max: 3 },
        { name: "route", start: () => d.route.reset(), tick: () => d.route.state().time > 19, max: 30 },
        { name: "lineup", start: () => d.shot("lineup"), tick: (t) => t - t0.s > 1.5, max: 4 },
      ];
      for (const s of segs) {
        const st = s.start;
        s.start = () => {
          t0.s = d.runtime.clock.time;
          st?.();
        };
      }
      runTour({
        kind: "figure-lab",
        renderer: d.renderer,
        scene: d.scene,
        composer: d.look.composer,
        game: { simT: () => d.runtime.clock.time, pause: (on) => void (d.runtime.paused = on), advanceTime: (ms, draw) => void d.runtime.advance(ms, { draw: draw ?? true }) },
        segments: segs,
        ffMs,
        route: "figure-lab",
        estSeconds: 25,
        tier: d.tier === "low" ? "low" : "high",
        ui,
        readyAt: performance.now(),
        quality: () => ({ ...d.look.report() }),
        extra: () => ({ cast: Object.fromEntries([...d.cast].map(([id, m]) => [id, m.fig.stats()])) }),
      });
    },
    /** The perf tour's report (null before a tour; status "running", then "done"). */
    perf: () => perfReport(),
  };
}

/** The lab's static world: the ground's triangles (palette-coloured facets), two steps on the loop, trees; one mesh. */
function buildGround(look: LowPolyLook, world: CollisionWorld): THREE.Mesh {
  const rng = seeded(4242);
  const pick = <T,>(a: readonly T[]): T => a[Math.floor(rng() * a.length)]!;
  const N = 48;
  const S = 48;
  const pos: number[] = [];
  const col: number[] = [];
  const v = (i: number, j: number): [number, number, number] => {
    const x = -S / 2 + (i / N) * S;
    const z = -S / 2 + (j / N) * S;
    return [x, labHeight(x, z), z];
  };
  const loop = LAB_ROUTE.loop;
  const onPath = (x: number, z: number): boolean => Math.abs(Math.hypot((x - loop.x) / loop.rx, (z - loop.z) / loop.rz) - 1) * Math.min(loop.rx, loop.rz) < 0.9;
  const grass = ["grass.a", "grass.b", "grass.c", "grass.d"].map((k) => look.palette(k));
  const path = ["path.a", "path.b"].map((k) => look.palette(k));
  for (let j = 0; j < N; j++)
    for (let i = 0; i < N; i++) {
      const a = v(i, j);
      const b = v(i + 1, j);
      const c = v(i, j + 1);
      const d = v(i + 1, j + 1);
      for (const tri of [[a, c, b], [b, c, d]] as [number, number, number][][]) {
        const cx = (tri[0]![0] + tri[1]![0] + tri[2]![0]) / 3;
        const cz = (tri[0]![2] + tri[1]![2] + tri[2]![2]) / 3;
        const k = onPath(cx, cz) ? pick(path) : pick(grass);
        for (const p of tri) {
          pos.push(...p);
          col.push(k.r, k.g, k.b);
        }
      }
    }
  const terrain = new THREE.BufferGeometry();
  terrain.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  terrain.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  terrain.setAttribute("glow", new THREE.Float32BufferAttribute(new Float32Array(pos.length / 3), 1));
  terrain.computeVertexNormals();
  world.add({ id: "lab:ground", kind: "fixed", colliders: [{ layer: "ground", tag: "grass", shape: { type: "trimesh", vertices: pos, indices: Array.from({ length: pos.length / 3 }, (_, i) => i) } }] });

  const parts: THREE.BufferGeometry[] = [terrain];
  // two steps across the loop
  STEPS.forEach((a, i) => {
    const x = loop.x + Math.cos(a) * loop.rx;
    const z = loop.z + Math.sin(a) * loop.rz;
    const tx = -Math.sin(a) * loop.rx;
    const tz = Math.cos(a) * loop.rz;
    const yaw = Math.atan2(tx, tz);
    const y = labHeight(x, z);
    const g = new THREE.BoxGeometry(2.2, STEP_H + 0.6, 1.1).translate(0, (STEP_H - 0.6) / 2, 0);
    g.rotateY(yaw).translate(x, y, z);
    parts.push(facet(g, { colour: look.palette(i ? "stone.a" : "stone.b"), jitter: 0, tint: 0.06, rng }));
    const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    world.add({ id: `lab:step${i}`, kind: "fixed", position: { x, y: y + (STEP_H - 0.6) / 2, z }, rotation: { x: q.x, y: q.y, z: q.z, w: q.w }, colliders: [{ layer: "ground", tag: "stone", shape: { type: "cuboid", halfExtents: { x: 1.1, y: (STEP_H + 0.6) / 2, z: 0.55 } } }] });
  });
  // trees around the rim
  for (let i = 0; i < 18; i++) {
    const a = (i / 18) * Math.PI * 2 + rng() * 0.2;
    const r = 13.5 + rng() * 4;
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    const y = labHeight(x, z);
    const s = 0.9 + rng() * 0.4;
    parts.push(facet(new THREE.CylinderGeometry(0.14 * s, 0.22 * s, 1.4 * s, 5).translate(x, y + 0.7 * s, z), { colour: look.palette("trunk"), jitter: 0.03, tint: 0.08, rng }));
    if (rng() < 0.5)
      for (let k = 0; k < 3; k++)
        parts.push(facet(new THREE.IcosahedronGeometry((0.6 + rng() * 0.35) * s, 0).translate(x + (rng() - 0.5) * 0.8, y + (1.9 + rng() * 0.6) * s, z + (rng() - 0.5) * 0.8), { colour: look.palette(pick(["leaf.a", "leaf.b", "leaf.c", "leaf.autumn"])), jitter: 0.1, tint: 0.08, rng }));
    else
      for (let k = 0; k < 4; k++)
        parts.push(facet(new THREE.ConeGeometry((1.1 - k * 0.24) * s, 0.9 * s, 6).translate(x, y + (1.4 + k * 0.55) * s, z), { colour: look.palette(pick(["pine.a", "pine.b", "leaf.c"])), jitter: 0.07, tint: 0.08, rng }));
  }
  // grass tufts
  for (let i = 0; i < 90; i++) {
    const x = (rng() - 0.5) * 30;
    const z = (rng() - 0.5) * 30;
    if (onPath(x, z) || Math.hypot(x, z) < 3) continue;
    parts.push(facet(new THREE.ConeGeometry(0.035, 0.25 + rng() * 0.2, 3).translate(x, labHeight(x, z) + 0.15, z), { colour: look.palette(pick(["grass.b", "grass.c", "leaf.autumn"])), rng }));
  }
  const mesh = new THREE.Mesh(merge(parts.map((p) => (p.index ? p.toNonIndexed() : p))), look.surface);
  mesh.name = "lab:world";
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}
